// apps/web/src/lib/painel/blocos.ts — Fase 16.1, Plano 08 (D-01, D-02, D-13, D-27).
//
// Quais blocos do painel a sessão vê e quais rotas ela pode abrir a partir deles.
// Ergonomia apenas: a autorização real é o 403 do servidor em cada endpoint de bloco e em cada rota (D-18).
import { ID_BLOCOS_PAINEL, type IdBlocoPainel } from '@pieltec/contracts';
import { ARVORE_NAVEGACAO, type GrupoNavegacao } from '../../components/shell/navegacao';

/** Gate de cada bloco por permissão de ação, nunca por nome de perfil (D-01). Lista = OR (D-27). */
export const GATE_BLOCO: Readonly<Record<IdBlocoPainel, readonly string[]>> = {
  pcp: ['planejamento:gerenciar'],
  qualidade: ['qualidade:gerenciar'],
  almoxarifado: ['estoque:separar', 'estoque:entregar'],
  compras: ['compras:gerenciar'],
  faturamento: ['pv:fechar'],
  'visao-geral': ['painel:consultar'],
};

/** Blocos visíveis para a sessão, sempre na ordem fixa de `ID_BLOCOS_PAINEL` (D-02). */
export function blocosVisiveis(permissoes: readonly string[]): IdBlocoPainel[] {
  const conjunto = new Set(permissoes);
  return ID_BLOCOS_PAINEL.filter((id) => GATE_BLOCO[id].some((p) => conjunto.has(p)));
}

/** Rotas protegidas que não aparecem no menu (a permissão vem do router). */
export const PERMISSAO_ROTA_FORA_DO_MENU: Readonly<Record<string, string>> = {
  '/producao/nova-op': 'producao:gerenciar',
  '/producao/ordens/preparar': 'producao:consultar',
  '/producao/ordens/detalhe': 'producao:consultar',
  '/painel': 'painel:consultar',
  '/compras/pedidos/novo': 'compras:gerenciar',
  // Tela de escrita de Vendas (router: PERMISSAO_ROTA_VENDAS); a paleta Ctrl+K só oferece "Novo PV" a quem a abre.
  '/vendas/pedidos/novo': 'pv:editar',
  // Detalhe do PV (aberto pelos itens "Faturar" da lista "Precisa de você"): o servidor exige pv:ler em cada leitura.
  '/vendas/pedidos/detalhe': 'pv:ler',
  // Detalhe do pedido de compra (aberto pelos itens "Prazo" e "Compra" de "Precisa de você", Plano 19): o servidor exige compras:consultar.
  '/compras/pedidos/detalhe': 'compras:consultar',
};

/**
 * Permissões que também abrem uma rota do menu além da exigida. L-69 (17.1-27): a lista de notas de compra abre para quem aprova a
 * proposta fiscal (Faturamento), que chega a ela pelos indicadores "Ver notas" do painel; o servidor aceita as duas leituras.
 */
export const PERMISSOES_ALTERNATIVAS_DA_ROTA: Readonly<Record<string, readonly string[]>> = {
  '/compras/notas': ['financeiro:aprovar-fiscal'],
};

function permissaoDaRota(caminho: string, arvore: readonly GrupoNavegacao[]): string | undefined {
  for (const grupo of arvore) {
    if (grupo.caminho === caminho && grupo.permissaoExigida) return grupo.permissaoExigida;
    const item = grupo.itens?.find((i) => i.caminho === caminho);
    if (item?.permissaoExigida) return item.permissaoExigida;
  }
  return PERMISSAO_ROTA_FORA_DO_MENU[caminho];
}

/**
 * A sessão tem a permissão da rota de destino? Remove a query string antes de procurar.
 * Fail-closed: rota desconhecida (ou sem permissão declarada) devolve false.
 */
export function podeAbrirRota(
  caminho: string,
  permissoes: readonly string[],
  arvore: readonly GrupoNavegacao[] = ARVORE_NAVEGACAO,
): boolean {
  const semQuery = caminho.split(/[?#]/, 1)[0] ?? '';
  const exigida = permissaoDaRota(semQuery, arvore);
  if (exigida === undefined) return false;
  return permissoes.includes(exigida) || (PERMISSOES_ALTERNATIVAS_DA_ROTA[semQuery] ?? []).some((p) => permissoes.includes(p));
}

/** URL do endpoint de um bloco (string de URL, não SQL). */
export function caminhoBlocoPainel(id: IdBlocoPainel): string {
  return `/api/v1/painel/blocos/${id}`;
}
